import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { Spinner } from 'ui/spinner';
import { pxToRem } from 'utils/convert';
import { useResizeObserver } from 'utils/hooks/useResizeObserver';

export const NODE_EDITOR_DRAWER_OVERLAY_TEST_ID = 'node-editor-drawer-overlay';
export const NODE_EDITOR_ASYNC_SPINNER_TEST_ID = 'node-editor-async-spinner';

const OverlayStyles = css`
  z-index: 100;
  position: absolute;
  width: 100%;
  left: 0;
  ${({ showBackground = true }: { showBackground?: boolean }) =>
    showBackground ? `background: rgba(255, 255, 255, 0.5)` : ``};
`;

const OverlayWrapper = styled.div`
  position: relative;
`;

const StyledSimpleOverlay = styled.div`
  top: 0;
  height: 100%;
  ${OverlayStyles}
`;

const StyledOverlay = styled.div<{ $hasNote: boolean; $height: number; showBackground: boolean }>`
  ${OverlayStyles}
  top: ${({ $hasNote }) => ($hasNote ? '0px' : 'auto')};
  height: ${({ $hasNote, $height }) => {
    // eslint-disable-next-line no-nested-ternary
    return $height
      ? $hasNote
        ? pxToRem($height + 24) // note needs to be included *below* overlay in published view
        : pxToRem($height) // any editors that are read-only but with no notes
      : '100%';
  }};
`;

type Children = React.ReactNode;

type OverlayContainerProps = {
  children: Children;
  hasNote?: boolean;
  showOverlay?: boolean;
  showSpinner?: boolean;
  showBackground?: boolean;
};

export const SimpleOverlayContainer = ({
  showOverlay,
  children,
  showSpinner,
}: OverlayContainerProps) => {
  const Wrapper = showOverlay ? OverlayWrapper : React.Fragment;
  return (
    <Wrapper>
      {showOverlay && (
        <StyledSimpleOverlay data-test={NODE_EDITOR_DRAWER_OVERLAY_TEST_ID}>
          {showSpinner && <Spinner data-test={NODE_EDITOR_ASYNC_SPINNER_TEST_ID} />}
        </StyledSimpleOverlay>
      )}
      {children}
    </Wrapper>
  );
};

export const OverlayContainer = ({
  children,
  hasNote = false,
  showBackground = true,
  showOverlay = false,
  showSpinner = false,
}: OverlayContainerProps) => {
  const drawerRef = useRef(null);
  const drawerSizes = useResizeObserver(drawerRef);

  return (
    <div ref={drawerRef}>
      {showOverlay && (
        <StyledOverlay
          data-test={NODE_EDITOR_DRAWER_OVERLAY_TEST_ID}
          $height={drawerSizes?.height}
          $hasNote={hasNote}
          showBackground={showBackground}
        >
          {showSpinner && <Spinner data-test={NODE_EDITOR_ASYNC_SPINNER_TEST_ID} />}
        </StyledOverlay>
      )}
      {children}
    </div>
  );
};
